import React, { useMemo } from 'react';
import { APITypes } from '../../primary-record-types';
import {
    TempAndPersistedAvatarState,
    avatarStateToAvatarChange,
} from '../v2BaseComponents/ImagePicker/TempImagePicker';
import { getIntl } from '../../utils/intl';
import { CARD_ASPECT_RATIO, CARD_UPLOAD_QUALITY } from '../../utils/constants';
import { CenteredFormRow, Form } from '../v2BaseComponents/Form';
import { handleFormikSubmit } from '../../utils/handleFormikSubmit';
import { FastField } from 'formik';
import { FormikInputs } from '../v2BaseComponents/Inputs';
import { getRequiredTempAvatar, required } from '../../utils/validators';
import { FormStack } from '../v2BaseComponents/Stack';

const intl = getIntl('CardForm', {
    headerTemplate: 'Add New {cardType}',
    id: 'ID Card',
    insurance: 'Insurance Card',
    description: '{cardType} Description',
    image: '{cardType} Image',
    save: 'Save',
    edit: 'Edit {cardType}',
    add: 'Add {cardType}',
});

type CardFormValue = {
    description: string;
    image: TempAndPersistedAvatarState | null;
};

export type CardValue = {
    description: string;
    imageChange: APITypes.sharedTypes.AvatarChange;
};

export type CardFormOwnProps = {
    onSubmit: (value: { description: string; imageChange: APITypes.sharedTypes.AvatarChange }) => void;
    initialDescription?: string;
    initialImageUrl?: string;
    isDisabled?: boolean;
    isLoading?: boolean;
    cardType: 'id' | 'insurance';
    onCancel?: () => void;
};

export type CardFormProps = CardFormOwnProps;

const CardForm: React.FC<CardFormProps> = (props) => {
    const initialValues = useMemo<CardFormValue>(() => {
        return {
            description: props.initialDescription || '',
            image: null, // updated by form component based on initialImageUrl
        };
    }, [props.initialDescription]);

    return (
        <Form
            isDisabled={props.isDisabled || props.isLoading}
            onCancel={props.onCancel}
            actionContainerSubmit
            title={intl(props.initialDescription ? 'edit' : 'add', {
                cardType: intl(props.cardType),
            })}
            onSubmit={(value, helpers) => {
                props.onSubmit({
                    description: value.description,
                    imageChange: avatarStateToAvatarChange(value.image),
                });
                handleFormikSubmit(value, helpers);
            }}
            initialValues={initialValues}
        >
            {() => (
                <FormStack>
                    <CenteredFormRow>
                        <FastField
                            component={FormikInputs.TempAndExistingImagePicker}
                            isRequired
                            name="image"
                            existingAvatarUrl={props.initialImageUrl}
                            validate={getRequiredTempAvatar(props.initialImageUrl)}
                            imagePickerOptions={{
                                allowsEditing: true,
                                aspect: CARD_ASPECT_RATIO,
                                quality: CARD_UPLOAD_QUALITY,
                                sizeVariant: 'id-card',
                            }}
                        />
                    </CenteredFormRow>
                    <CenteredFormRow>
                        <FastField
                            component={FormikInputs.Input}
                            title={intl('description', { cardType: intl(props.cardType) })}
                            isRequired
                            validate={required}
                            isDisabled={props.isDisabled || props.isLoading}
                            name="description"
                        />
                    </CenteredFormRow>
                </FormStack>
            )}
        </Form>
    );
};

export default React.memo(CardForm);
